import { usersApi } from "#/api/usersApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import { ClientUserPermission, type User, UserStatus } from "#/types/user";
import { sanitize } from "#/utils/StringUtils";
import { Select, Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteClientUserModal from "./DeleteClientUserModal";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

export interface Props {
  clientId: number;
}

const UsersTable = ({ clientId }: Props) => {
  const { t } = useTranslation();
  const { canWrite, canDelete } = usePermission();
  const [page, setPage] = useState<number>(1);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const headers = [
    { key: "id", value: t("clients.users.columns.id") },
    { key: "name", value: t("clients.users.columns.name") },
    { key: "email", value: t("clients.users.columns.email") },
    { key: "permission", value: t("clients.users.columns.permission") },
    { key: "status", value: t("clients.users.columns.status") },
    { key: "Action", value: t("clients.users.columns.action") },
  ];
  const [sortBy, setSortBy] = useState<string>("createdAt");

  const [
    removeUserModalOpened,
    { open: openRemoveUserModal, close: closeRemoveUserModal },
  ] = useDisclosure(false);
  const [userToRemove, setUserToRemove] = useState<User>();

  const {
    data: { items = [], total = 0, limit = 0 } = {},
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["users", clientId, sortDirection, sortBy],

    queryFn: async () => {
      try {
        const response = await usersApi.getAll({
          clientId: clientId.toString(),
        });

        return {
          items: response.data.data,
          total: response.data.total,
          limit: response.data.limit,
        };
      } catch (error) {
        console.log("🚀 ~ queryFn: ~ error:", error);
        throw new Error("No users returned");
      }
    },
    enabled: !!clientId,
    staleTime: 1000 * 60,
    retry: false,
  });

  const updatePermissionMutation = useMutation({
    mutationFn: async ({ userId, permission }: { userId: string; permission: ClientUserPermission }) => {
      return await usersApi.update(userId, { clientPermission: permission });
    },
    onSuccess: () => {
      void refetch();
    },
    onError: (error) => {
      console.error("🚀 ~ updatePermission ~ error:", error);
    },
  });

  return (
    <>
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 overflow-auto min-h-0">
          <BaseTable
            headers={headers.map((item) => item.value)}
            loading={isPending}
            allowSorting={false}
            sortableColumns={[1, 2, 3]}
            onSortChange={(e: TableSortProps) => {
              setPage(1);
              setSortDirection(e.direction);
              setSortBy(headers[e.index].key);
            }}
            defaultSort={{
              direction: "DESC",
              index: 5,
            }}
            rows={items?.map((item) => {
              return (
                <Table.Tr key={item.id}>
                  <Table.Td className="w-10 max-w-40">
                    {item.id ?? "--"}
                  </Table.Td>
                  <Table.Td className="capitalize">
                    {item.name ?? "--"}
                  </Table.Td>
                  <Table.Td>{item?.email ?? "--"}</Table.Td>
                  <Table.Td style={{ width: '130px' }}>
                    <Select
                      value={item?.clientPermission || ClientUserPermission.FULL}
                      onChange={(value) => {
                        if (value) {
                          updatePermissionMutation.mutate({
                            userId: item.id,
                            permission: value as ClientUserPermission
                          });
                        }
                      }}
                      data={Object.values(ClientUserPermission).map((permission) => ({
                        label: sanitize(permission),
                        value: permission,
                      }))}
                      size="xs"
                      disabled={item.status === UserStatus.DEACTIVATED || !canWrite()}
                      comboboxProps={{ width: 130 }}
                    />
                  </Table.Td>
                  <Table.Td
                    className={`uppercase text-sm font-bold ${item.status === UserStatus.DEACTIVATED ? "text-red-500" : "text-teal-500"}`}
                  >
                    {item?.status ?? "--"}
                  </Table.Td>
                  <Table.Td className="w-24">
                    <div className="w-full flex items-center justify-center">
                      {item.status !== UserStatus.DEACTIVATED ? (
                        <PermissionTooltip disabled={!canDelete()}>
                          <ActionButton
                            icon={IconTrash}
                            variant="outline"
                            color="red"
                            onClick={() => {
                              setUserToRemove(item);
                              openRemoveUserModal();
                            }}
                            disabled={!canDelete()}
                          />
                        </PermissionTooltip>
                      ) : (
                        "--"
                      )}
                    </div>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          />
        </div>
        {total > 0 && (
          <div className="w-full bg-white border-t border-gray-200 py-2 flex-shrink-0">
            <Pagination
              page={page}
              total={total}
              limit={limit}
              onChange={setPage}
            />
          </div>
        )}
      </div>

      <DeleteClientUserModal
        opened={removeUserModalOpened}
        onClose={closeRemoveUserModal}
        user={userToRemove!}
        onSuccess={() => {
          void refetch();
          closeRemoveUserModal();
        }}
      />
    </>
  );
};

export default UsersTable;
